import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Badge,
  IconButton,
  Menu,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import { useNavigate } from 'react-router-dom';
import { deleteUserNotification, deleteUserNotificationsByIds, loadUserNotifications, markUserNotificationAsRead, markUserNotificationsAsReadByIds, UserNotification } from './userNotificationAPI';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectUser } from '../user/userSlice';
import { Localized } from '@fluent/react';
import { selectSettings } from '../settings/settingsSlice';

export const NotificationPanel = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { tokenState } = useAppSelector(selectUser);

  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const intRef = useRef<null | NodeJS.Timeout>(null);
  const [busy, setBusy] = useState<'none' | 'mark' | 'delete'>('none');
  const { locale } = useAppSelector(selectSettings);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const fetchNotifications = async () => {
    try {
      const userNotifications = await dispatch(loadUserNotifications()).unwrap();
      setNotifications(userNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const startPolling = useCallback(() => {
    if (intRef.current === null) {
      fetchNotifications();
      const interval = setInterval(() => {
        fetchNotifications();
      }, 30000);
      intRef.current = interval;
    }
  }, []);

  const clearPolling = useCallback(() => {
    if (intRef.current) {
      clearInterval(intRef.current);
      intRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (tokenState && tokenState.tag === 'valid') {
      startPolling();
    } else {
      clearPolling();
    }
  }, [tokenState, startPolling, clearPolling]);

  useEffect(() => () => clearPolling(), [clearPolling]);

  const handleNotificationClick = async (notification: UserNotification) => {
    try {
      await dispatch(markUserNotificationAsRead(notification.id)).unwrap();
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n));
      navigate(notification.link,{state: { open: 'comments', nonce: Date.now() }});
      handleCloseMenu();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const formatDateTime = (iso: string) => new Date(iso).toLocaleString();


  const formatRelative = (iso: string) => {
    const d = new Date(iso);
    const diff = d.getTime() - Date.now();
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

    const sec = Math.round(diff / 1000);
    const min = Math.round(sec / 60);
    const hr = Math.round(min / 60);
    const day = Math.round(hr / 24);

    if (Math.abs(sec) < 60) return rtf.format(sec, "second");
    if (Math.abs(min) < 60) return rtf.format(min, "minute");
    if (Math.abs(hr) < 24) return rtf.format(hr, "hour");
    return rtf.format(day, "day");
  };

  const handleDeleteNotification = async (id: string) => {

    try {
      await dispatch(deleteUserNotification(id)).unwrap();
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };


  const handleDeleteVisible = async () => {
    const ids = visible.map(n => n.id);
    if (!ids.length) return;

    const prev = notifications;
    setBusy('delete');


    try {
      await dispatch(deleteUserNotificationsByIds(ids)).unwrap();
      setNotifications(p => p.filter(n => !ids.includes(n.id)));

    } catch (e) {
      console.error(e);
      setNotifications(prev);
    } finally {
      setBusy('none');
    }
  };

  const handleMarkVisibleAsRead = async () => {
    const ids = visible.filter(n => !n.isRead).map(n => n.id);
    if (!ids.length) return;

    const prev = notifications;
    setBusy('mark');


    try {
      await dispatch(markUserNotificationsAsReadByIds(ids)).unwrap();
      setNotifications(p => p.map(n => ids.includes(n.id) ? { ...n, isRead: true } : n));

    } catch (e) {
      console.error(e);
      setNotifications(prev);
    } finally {
      setBusy('none');
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const visible = React.useMemo(() => {
    let data = [...notifications];
    if (filter === 'unread') data = data.filter(n => !n.isRead);
    data.sort((a, b) => Number(a.isRead) - Number(b.isRead));
    return data;
  }, [notifications, filter]);

  const statusIcon = (isRead: boolean) =>
    isRead ? <MarkUnreadChatAltIcon fontSize="small" color="disabled" /> : <MarkUnreadChatAltIcon fontSize="small" color="primary" />;

  return (
    <Box>
      <Tooltip title={<Localized id='notification-title'>_notifications</Localized>}>
        <IconButton
          color="inherit"
          aria-label="Open notifications"
          aria-haspopup
          aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
          onClick={handleOpenMenu}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { width: 420, maxHeight: 460, borderRadius: 3 } },
        }}
      >
        <Box
          px={1.5}
          pt={1.25}
          pb={0.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            <Localized id="notification-title">_notifications</Localized>
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_, v) => v && setFilter(v)}
          >
            <Tooltip
              title={
                <span>
                  <Localized id="notification-show-all">_all</Localized>{' '}
                  {` (${notifications.length})`}
                </span>
              }
            >
              <ToggleButton value="all" sx={{ px: 1 }}>
                <ListIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip
              title={
                <span>
                  <Localized id="notification-show-unread">_unread</Localized>{' '}
                  {` (${unreadCount})`}
                </span>
              }
            >
              <ToggleButton value="unread" sx={{ px: 1 }}>
                <MarkUnreadChatAltIcon fontSize="small" color="primary" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
        <Divider />

        {/* Lista */}
        {visible.length === 0 ? (
          <Box px={2} py={2.5} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              <Localized id="notification-list-empty">_empty</Localized>
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {visible.map(n => (
              <ListItem
                key={n.id}
                disablePadding
                sx={{
                  px: 1,
                  position: 'relative',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 6,
                    bottom: 6,
                    width: 2,
                    borderRadius: 1,
                    bgcolor: n.isRead ? 'divider' : 'primary.main',
                  },
                }}
                secondaryAction={
                  <Tooltip title={<Localized id="delete">_delete</Localized>}>
                    <IconButton
                      edge="end"
                      aria-label="delete notification"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(n.id);
                      }}
                      size="small"
                      sx={{
                        "& .MuiSvgIcon-root": { transition: "color .15s ease" },
                        "&:hover .MuiSvgIcon-root, &:focus-visible .MuiSvgIcon-root": {
                          color: "error.main",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemButton
                  onClick={() => handleNotificationClick(n)}
                  sx={{ py: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {statusIcon(!!n.isRead)}
                  </ListItemIcon>
                  <ListItemText
                    primary={n.message}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: {
                        fontWeight: n.isRead ? 400 : 700,
                        fontStyle: n.isRead ? 'italic' : 'normal',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      },
                    }}
                    secondary={
                      <span>
                        {formatRelative(n.createdAt)} • {formatDateTime(n.createdAt)}
                      </span>
                    }
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        <Divider />
        <Box
          px={1.25}
          py={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Tooltip title={<Localized id="notification-mark-all-as-read">_mark-all-as-read</Localized>}>
            <span>
              <Button
                size="small"
                onClick={handleMarkVisibleAsRead}
                disabled={busy !== 'none' || !visible.some(n => !n.isRead)}
              >
                <Localized id="notification-mark-all-as-read">_mark-all-as-read</Localized>
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={<Localized id="notification-delete-all">_delete-all</Localized>}>
            <span>
              <Button
                size="small"
                color="inherit"
                onClick={handleDeleteVisible}
                disabled={busy !== 'none' || visible.length === 0}
              >
                <Localized id="notification-delete-all">_delete-all</Localized>
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Menu>
    </Box>
  );
};
